import { FC, useState } from 'react';
import {
  Stack,
  Text,
  SimpleGrid,
  Group,
  Menu,
  ActionIcon,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  BirthMeasurementsData,
  ListContentAction,
} from '../utils/medical-history.types';
import { LabeledField } from '@/components/LabeledField';
import { IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import DeleteConfirmModal from '../../images-tab/DeleteModal';
export interface BirthMeasurementsListContentProps {
  data: BirthMeasurementsData;
  onAction?: (action: ListContentAction) => void;
  readonly?: boolean;
}

export const BirthMeasurementsListContent: FC<
  BirthMeasurementsListContentProps
> = ({ data, onAction = () => {}, readonly = false }) => {
  const { t } = useTranslation(['patient-pages']);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  const hasWeight = !!data.birthWeight?.valueQuantity?.value;
  const hasLength = !!data.birthLength?.valueQuantity?.value;
  const hasHeadCircumference =
    !!data.birthHeadCircumference?.valueQuantity?.value;
  const hasData = hasWeight || hasLength || hasHeadCircumference;

  const handleDeleteClick = () => {
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteModalOpened(false);
    onAction('remove');
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpened(false);
  };

  return (
    <>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap="xs" flex={1}>
          <Text fw={600} size="sm">
            {t('medicalHistory.birthMeasurements.title', {
              defaultValue: 'Birth Measurements',
            })}
          </Text>
          {/* Empty state */}
          {!hasData ? (
            <Text size="sm" c="dimmed" ta="center">
              {t('medicalHistory.birthMeasurements.noData', {
                defaultValue: 'No birth measurements recorded',
              })}
            </Text>
          ) : (
            <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
              <LabeledField
                label={t('medicalHistory.birthMeasurements.weight', {
                  defaultValue: 'Weight',
                })}
                value={
                  hasWeight
                    ? `${data.birthWeight?.valueQuantity?.value}gr`
                    : '-'
                }
              />
              <LabeledField
                label={t('medicalHistory.birthMeasurements.length', {
                  defaultValue: 'Length',
                })}
                value={
                  hasLength
                    ? `${data.birthLength?.valueQuantity?.value}cm`
                    : '-'
                }
              />

              {/* Row 2: Head Circumference */}
              <LabeledField
                label={t('medicalHistory.birthMeasurements.headCircumference', {
                  defaultValue: 'Head Circumference',
                })}
                value={
                  hasHeadCircumference
                    ? `${data.birthHeadCircumference?.valueQuantity?.value}cm`
                    : '-'
                }
              />
            </SimpleGrid>
          )}
        </Stack>

        {/* Actions menu or loading indicator */}
        {hasData && !readonly && (
          <Menu shadow="md" width={160} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={() => onAction('edit')}
              >
                {t('edit', {
                  ns: 'common',
                  defaultValue: 'Edit',
                })}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                onClick={handleDeleteClick}
                color="red"
              >
                {t('delete', {
                  ns: 'common',
                  defaultValue: 'Delete',
                })}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      <DeleteConfirmModal
        opened={deleteModalOpened}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('medicalHistory.birthMeasurements.confirmDelete.title', {
          ns: 'patient-pages',
          defaultValue: 'Delete birth measurements data?',
        })}
        message={t('medicalHistory.birthMeasurements.confirmDelete.message', {
          ns: 'patient-pages',
          defaultValue:
            'Are you sure you want to delete this birth measurements data? This action cannot be undone.',
        })}
      />
    </>
  );
};

export default BirthMeasurementsListContent;
